import React, { useCallback, useEffect, useState } from 'react'

import { flatMap } from 'lodash'
import { setActiveFilterId, setDateRange } from 'slices/filters'

import { Box, Button, Flex, Icon } from '@driftt/dds'
import { faFilter } from '@driftt/dds.icons/light/faFilter'

import { DateRange, getDefaultRange } from 'components/DateRangePicker'
import NotificationSettings from 'components/NotificationSettings'
import ActiveFilters from 'components/SortAndFilter/ActiveFilters'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import SearchBar from 'pages/ActivityDashboard/SearchBar'
import CCRMFiltersCard from 'sections/CCRM/FiltersCard'
import { FilterPage } from 'utils/filters'

interface Props {
  onOpenEditModal: () => void
  setActiveFilterId: (id: number | undefined) => void
}

const CCRMHeaderBar = (props: Props) => {
  const [showFiltersCard, setShowFiltersCard] = useState(false)
  const dispatch = useAppDispatch()
  const { searchQuery, dateRange, filterGroupsByPage } = useAppSelector(({ filters }) => filters)

  const attributeFilters = useAttributeFilters()
  const filterConfig = useFilterConfiguration(attributeFilters)

  const onDateRangeChange = useCallback(
    (dateRange: DateRange | undefined) => {
      dispatch(setDateRange(dateRange))
    },
    [dispatch]
  )

  const handleSetActiveFilterId = (id: number | undefined = -1) => {
    dispatch(setActiveFilterId(id))
  }

  useEffect(() => {
    const interval = setInterval(() => {
      // Clear date range, if present, and value input value is present
      if (dateRange && searchQuery) {
        onDateRangeChange(undefined)
      }
      // Reset default date range once inputs value is empty and dateRange is undefined
      if (!searchQuery && !dateRange) {
        onDateRangeChange(getDefaultRange())
      }
    }, 300)

    return () => clearInterval(interval)
  }, [onDateRangeChange, dateRange, dispatch, searchQuery])


  const filterGroups: any[] = flatMap(filterGroupsByPage.ACTIVITIES)

  return (
    <Flex direction="column" gap={2}>
      <Flex
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        css={{ minWidth: '600px' }}
      >
        <SearchBar />
        <Flex gap={1} alignItems="center" justifyContent="center">
          <Box css={{position: 'relative'}}>
            <Button
              variant="secondary"
              onClick={() => { setShowFiltersCard(!showFiltersCard) }}>
              <Icon icon={faFilter} /> {filterGroups.length ? `${filterGroups.length} `: ''}
              Filters
            </Button>

            {showFiltersCard && (
              <Box
                css={{
                  top: 0,
                  right: 0,
                  flex: 1,
                  width: 720,
                  position: 'absolute',
                  zIndex: '$modal'
                }}>
                <CCRMFiltersCard
                  onClose={() => setShowFiltersCard(false)}
                  onOpenEditModal={props.onOpenEditModal}
                  setActiveFilterId={handleSetActiveFilterId}
                  useDefaultStyles
                />
              </Box>
            )}
          </Box>
          <NotificationSettings />
        </Flex>
      </Flex>
      <Flex
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        css={{ minWidth: '600px' }}
        >
        <Flex
          alignItems="center"
          css={{
            minHeight: 'fit-content'
          }}>
          {filterGroups.length > 0 && (
            <ActiveFilters filterConfiguration={filterConfig} filterPage={FilterPage.ACTIVITIES} />
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default CCRMHeaderBar
